import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { avatars } from '@/config/siteImages';

export default function ResultsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const isEs = location.pathname.startsWith('/es');

  const caseStudies = [
    {
      key: '1',
      avatar: avatars.small.cs72a,
    },
    {
      key: '2',
      avatar: avatars.small.cs72b,
    },
    {
      key: '3',
      avatar: avatars.small.cs72c,
    },
    {
      key: '4',
      avatar: avatars.small.cs72d,
    },
    {
      key: '5',
      avatar: avatars.small.cs72e,
    },
    {
      key: '6',
      avatar: avatars.small.cs72f,
    },
  ];

  const stats = [
    { value: t('results_stat_1_value'), label: t('results_stat_1_label'), icon: 'ri-funds-box-line' },
    { value: t('results_stat_2_value'), label: t('results_stat_2_label'), icon: 'ri-store-2-line' },
    { value: t('results_stat_3_value'), label: t('results_stat_3_label'), icon: 'ri-time-line' },
    { value: t('results_stat_4_value'), label: t('results_stat_4_label'), icon: 'ri-percent-line' },
  ];

  useEffect(() => {
    if (isEs) {
      document.title = 'Historias de Éxito | Resultados Reales de Clientes | Kailac';
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', 'Resultados reales de clientes calificados de Kailac. Dueños de negocios en múltiples industrias accedieron a financiamiento al 0% de interés con guía experta. Los resultados individuales pueden variar. Sujeto a elegibilidad.');
    } else {
      document.title = 'Success Stories | Business Funding Results | Kailac';
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', 'Real results from qualified Kailac clients. Business owners across industries accessed 0% interest funding with expert guidance. Individual results may vary. Subject to eligibility.');
    }
  }, [isEs]);

  return (
    <div className="min-h-screen bg-[#FCFAFD] pb-16 lg:pb-0">
      <Navbar />

      {/* Hero */}
      <section className="bg-[#F8F3FB] pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-4">{t('results_eyebrow')}</p>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-5 leading-tight">
            {t('results_hero_title_1')}<br />
            <span className="text-[#4F2D63]">{t('results_hero_title_2')}</span>
          </h1>
          <p className="text-base text-gray-500 max-w-2xl mx-auto mb-4">{t('results_hero_subtitle')}</p>
          <p className="text-xs text-gray-400 italic">{t('results_hero_compliance')}</p>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="py-12 bg-white border-b border-[#E9D9F2] px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            {stats.map(({ value, label, icon }) => (
              <div key={label}>
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F8F3FB] mx-auto mb-3">
                  <i className={`${icon} text-[#4F2D63] text-lg`}></i>
                </div>
                <p className="text-4xl font-bold text-gray-900 mb-1">{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-center text-gray-400 italic mt-4">{t('results_stats_note')}</p>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">{t('results_section_title')}</h2>
            <p className="text-base text-gray-500">{t('results_section_subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-7">
            {caseStudies.map(({ key, avatar }) => (
              <div key={key} className="bg-white rounded-2xl border border-[#E9D9F2] overflow-hidden">
                <div className="bg-[#F8F3FB] p-6 border-b border-[#E9D9F2]">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                      <img src={avatar} alt={t(`results_cs_${key}_name`)} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900">{t(`results_cs_${key}_name`)}</h3>
                      <p className="text-sm text-gray-500">{t(`results_cs_${key}_business`)} — {t(`results_cs_${key}_location`)}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs bg-[#E9D9F2] text-[#4F2D63] px-2.5 py-1 rounded-full font-semibold">
                          {t(`results_cs_${key}_amount`)} {t('results_guided')}
                        </span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">{t(`results_cs_${key}_plan`)}</span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">{t(`results_cs_${key}_timeline`)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>)}
                  </div>
                  <blockquote className="text-gray-700 text-sm leading-relaxed italic mb-5">
                    &ldquo;{t(`results_cs_${key}_quote`)}&rdquo;
                  </blockquote>
                  <div className="space-y-3 pt-4 border-t border-gray-100">
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{t('results_challenge')}</p>
                      <p className="text-sm text-gray-600">{t(`results_cs_${key}_challenge`)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-wider mb-1">{t('results_outcome')}</p>
                      <p className="text-sm text-gray-700 font-medium">{t(`results_cs_${key}_result`)}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 italic mt-4">{t('results_disclaimer')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance notice */}
      <section className="py-10 bg-amber-50 border-y border-amber-200 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 flex-shrink-0">
              <i className="ri-information-line text-amber-700 text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-900 mb-1">{t('results_disclosure_title')}</p>
              <p className="text-sm text-amber-800 leading-relaxed">{t('results_disclosure_body')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('results_cta_title')}</h2>
          <p className="text-gray-500 mb-8">{t('results_cta_subtitle')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to={isEs ? '/es/contacto' : '/contact'}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#4F2D63] text-white font-semibold hover:bg-[#3E234F] transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('results_cta_primary')} <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to={isEs ? '/es/como-funciona' : '/how-it-works'}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('results_cta_secondary')}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
